import {
	ClickAwayListener,
	Popper as PopperMui, styled,
} from '@mui/material';
import { VirtualElement } from '@popperjs/core';
import { ChildrenProps, appendClassName } from '@componentLibraryWrapper/common';
import { useState } from 'react';

export interface PopperProps extends ChildrenProps<HTMLDivElement> {
	anchorEl?: HTMLElement | VirtualElement | (() => HTMLElement) | (() => VirtualElement) | null,
	arrow?: boolean,
	onClickAway?: (event: MouseEvent | TouchEvent) => void,
	open?: boolean,
	placement?: 'auto-end'
		| 'auto-start'
		| 'auto'
		| 'bottom-end'
		| 'bottom-start'
		| 'bottom'
		| 'left-end'
		| 'left-start'
		| 'left'
		| 'right-end'
		| 'right-start'
		| 'right'
		| 'top-end'
		| 'top-start'
		| 'top'
}

const PopperStyled = styled(PopperMui, {
  shouldForwardProp: (prop) => prop !== 'arrow',
})(({ theme }) => ({
  zIndex: 1,
  '& > div': {
    position: 'relative',
  },
  '&[data-popper-placement*="bottom"]': {
    '& > div': {
      marginTop: 2,
    },
    '& .MuiPopper-arrow': {
      top: 0,
      left: 0,
      marginTop: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '0 1em 1em 1em',
        borderColor: `transparent transparent ${theme.palette.background.default} transparent`,
      },
    },
  },
  '&[data-popper-placement*="top"]': {
    '& > div': {
      marginBottom: 2,
    },
    '& .MuiPopper-arrow': {
      bottom: 0,
      left: 0,
      marginBottom: '-0.9em',
      width: '3em',
      height: '1em',
      '&::before': {
        borderWidth: '1em 1em 0 1em',
        borderColor: `${theme.palette.background.default} transparent transparent transparent`,
      },
    },
  },
  '&[data-popper-placement*="right"]': {
    '& > div': {
      marginLeft: 2,
    },
    '& .MuiPopper-arrow': {
      left: 0,
      marginLeft: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 1em 1em 0',
        borderColor: `transparent ${theme.palette.background.default} transparent transparent`,
      },
    },
  },
  '&[data-popper-placement*="left"]': {
    '& > div': {
      marginRight: 2,
    },
    '& .MuiPopper-arrow': {
      right: 0,
      marginRight: '-0.9em',
      height: '3em',
      width: '1em',
      '&::before': {
        borderWidth: '1em 0 1em 1em',
        borderColor: `transparent transparent transparent ${theme.palette.background.default}`,
      },
    },
  },
}));

const Arrow = styled('div')({
  position: 'absolute',
  fontSize: 7,
  width: '3em',
  height: '3em',
  '&::before': {
    content: '""',
    margin: 'auto',
    display: 'block',
    width: 0,
    height: 0,
    borderStyle: 'solid',
  },
});

const Popper = ({
	arrow=false,
	children,
	className,
	onClickAway,
	open=false,
	placement='bottom',
	...rest
}: PopperProps) => {
	const [arrowRef, setArrowRef] = useState<HTMLDivElement | null>(null);

	const popperStyled = 
	<PopperStyled
		{...rest}
		className={appendClassName('sm-popper', className)}
		open={open}
		placement={placement}
		modifiers={[
			{
				name: 'arrow',
				enabled: arrow,
				options: {
					element: arrowRef,
				},
			}
		]}>
		<div>
			{arrow ? (<Arrow ref={setArrowRef} className="MuiPopper-arrow"/>) : null}
			{children}
		</div>
	</PopperStyled>

	return (
		<>
		{onClickAway ? 
			(<ClickAwayListener
				onClickAway={onClickAway}
        mouseEvent='onMouseUp'>
				{popperStyled}
			</ClickAwayListener>) :
			{popperStyled}}
		</>
	)
}
export default Popper;